import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { Brain, HeartHandshake, Flower2, Infinity as InfinityIcon, MessageCircle, Handshake } from 'lucide-react';
import { 
  ArrowLeft, Loader2, Heart, Users, Sparkles, 
  Star, TrendingUp, AlertCircle, CheckCircle, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ShubhHeader from '../components/shubh/ShubhHeader';
import ShubhBottomNav from '../components/shubh/ShubhBottomNav';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

const RELATIONSHIP_COLORS = {
  self: 'from-[#C84B32] to-[#E85D43]',
  spouse: 'from-[#E91E63] to-[#F48FB1]',
  child: 'from-[#00BCD4] to-[#80DEEA]',
  parent: 'from-[#9C27B0] to-[#CE93D8]',
  sibling: 'from-[#4CAF50] to-[#A5D6A7]',
  other: 'from-[#FF9800] to-[#FFCC80]'
};

const COMPATIBILITY_ASPECTS = [
  { key: 'mental', label: 'Mental Compatibility', icon: Brain, description: 'Intellectual connection and communication' },
  { key: 'emotional', label: 'Emotional Bond', icon: HeartHandshake, description: 'Emotional understanding and support' },
  { key: 'spiritual', label: 'Spiritual Harmony', icon: Flower2, description: 'Shared values and spiritual growth' },
  { key: 'karmic', label: 'Karmic Connection', icon: InfinityIcon, description: 'Past life connections and destiny' },
  { key: 'communication', label: 'Communication', icon: MessageCircle, description: 'Ease of understanding each other' },
  { key: 'support', label: 'Mutual Support', icon: Handshake, description: 'Ability to support each other' }
];

export const FamilyCompatibilityPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [profile1, setProfile1] = useState(null);
  const [profile2, setProfile2] = useState(null);
  const [compatibility, setCompatibility] = useState(null);
  const [expandedAspect, setExpandedAspect] = useState(null);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/profiles`, { withCredentials: true });
      setProfiles(response.data);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeCompatibility = async () => {
    if (!profile1 || !profile2 || profile1 === profile2) return;
    
    setAnalyzing(true);
    setCompatibility(null);
    
    try {
      const response = await axios.post(
        `${API}/compatibility/family`,
        { profile_id_1: profile1, profile_id_2: profile2 },
        { withCredentials: true }
      );
      setCompatibility(response.data);
    } catch (error) {
      console.error('Error analyzing compatibility:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const getProfileById = (id) => profiles.find(p => p.profile_id === id);
  
  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Exceptional';
    if (score >= 80) return 'Excellent';
    if (score >= 70) return 'Very Good';
    if (score >= 60) return 'Good';
    if (score >= 50) return 'Moderate';
    if (score >= 40) return 'Fair';
    return 'Needs Attention';
  };

  if (loading) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="fixed inset-0 stars-bg opacity-30 pointer-events-none" />
        <div className="text-center relative z-10">
          <Heart className="w-12 h-12 text-[#E91E63] animate-pulse mx-auto mb-4" />
          <p className="text-[#6B6560]">Loading family members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen cosmic-bg pb-28">
      {/* Background */}
      <div className="fixed inset-0 stars-bg opacity-30 pointer-events-none" />
      <div className="fixed inset-0 nebula-bg pointer-events-none" />

      {/* Navigation */}
      <ShubhHeader title="Family Compatibility" showBack />

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Selection Section */}
        <div className="cosmic-card p-6 mb-8">
          <h3 className="font-['Fraunces'] text-xl text-[#282622] mb-6 text-center">
            Select Two Family Members to Compare
          </h3>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            {/* Profile 1 */}
            <div className="w-full md:w-48">
              <Select value={profile1 || ''} onValueChange={setProfile1}>
                <SelectTrigger 
                  className="bg-white border-[#282622]/10 text-[#282622] h-14"
                  data-testid="compatibility-profile-1"
                >
                  <SelectValue placeholder="First Person" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#282622]/10">
                  {profiles.map(profile => (
                    <SelectItem 
                      key={profile.profile_id} 
                      value={profile.profile_id} 
                      className="text-[#282622]"
                      disabled={profile.profile_id === profile2}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${RELATIONSHIP_COLORS[profile.relationship] || RELATIONSHIP_COLORS.other} flex items-center justify-center text-xs text-white font-bold`}>
                          {getInitials(profile.name)}
                        </div>
                        {profile.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Heart Icon */}
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E91E63] to-[#F5B942] flex items-center justify-center shadow-lg shadow-[#E91E63]/30">
                <Heart className="w-8 h-8 text-white" fill="white" />
              </div>
              <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-[#C84B32] animate-pulse" />
            </div>

            {/* Profile 2 */}
            <div className="w-full md:w-48">
              <Select value={profile2 || ''} onValueChange={setProfile2}>
                <SelectTrigger 
                  className="bg-white border-[#282622]/10 text-[#282622] h-14"
                  data-testid="compatibility-profile-2"
                >
                  <SelectValue placeholder="Second Person" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#282622]/10">
                  {profiles.map(profile => (
                    <SelectItem 
                      key={profile.profile_id} 
                      value={profile.profile_id} 
                      className="text-[#282622]"
                      disabled={profile.profile_id === profile1}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${RELATIONSHIP_COLORS[profile.relationship] || RELATIONSHIP_COLORS.other} flex items-center justify-center text-xs text-white font-bold`}>
                          {getInitials(profile.name)}
                        </div>
                        {profile.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-center mt-6">
            <Button
              onClick={analyzeCompatibility}
              disabled={!profile1 || !profile2 || profile1 === profile2 || analyzing}
              className="bg-gradient-to-r from-[#E91E63] to-[#F5B942] text-white hover:from-[#E91E63]/90 hover:to-[#F5B942]/90 px-8"
              data-testid="analyze-compatibility-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Analyzing Cosmic Connection...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analyze Compatibility
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Analyzing State */}
        {analyzing && (
          <div className="cosmic-card p-12 text-center">
            <div className="relative w-32 h-32 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-[#E91E63]/20" />
              <div className="absolute inset-0 rounded-full border-4 border-[#E91E63] border-t-transparent animate-spin" />
              <div className="absolute inset-4 rounded-full border-4 border-[#C84B32]/20" />
              <div className="absolute inset-4 rounded-full border-4 border-[#C84B32] border-b-transparent animate-spin-reverse" />
              <Heart className="absolute inset-0 m-auto w-10 h-10 text-[#E91E63] animate-pulse" />
            </div>
            <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-2">
              Reading the Stars
            </h3>
            <p className="text-[#6B6560]">
              Analyzing the cosmic connection between {getProfileById(profile1)?.name} and {getProfileById(profile2)?.name}...
            </p>
          </div>
        )}

        {/* Results */}
        {compatibility && !analyzing && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Overall Score */}
              <div className="cosmic-card p-8 text-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#E91E63]/10 via-transparent to-[#F5B942]/10" />
                
                <div className="relative z-10">
                  <div className="flex justify-center items-center gap-6 mb-6">
                    <div className="text-center">
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${RELATIONSHIP_COLORS[getProfileById(profile1)?.relationship] || RELATIONSHIP_COLORS.other} flex items-center justify-center mx-auto mb-2 shadow-lg`}>
                        <span className="text-white font-bold text-lg">
                          {getInitials(getProfileById(profile1)?.name)}
                        </span>
                      </div>
                      <p className="text-[#282622] font-medium">{getProfileById(profile1)?.name}</p>
                      <p className="text-[#6B6560] text-xs">{getProfileById(profile1)?.relationship || 'Family'}</p>
                    </div>
                    
                    <div className="relative">
                      <div className={`text-6xl font-bold ${getScoreColor(compatibility.overall_score)}`}>
                        {compatibility.overall_score}%
                      </div>
                      <p className="text-[#C84B32] text-sm mt-1">{getScoreLabel(compatibility.overall_score)}</p>
                    </div>
                    
                    <div className="text-center">
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${RELATIONSHIP_COLORS[getProfileById(profile2)?.relationship] || RELATIONSHIP_COLORS.other} flex items-center justify-center mx-auto mb-2 shadow-lg`}>
                        <span className="text-white font-bold text-lg">
                          {getInitials(getProfileById(profile2)?.name)}
                        </span>
                      </div>
                      <p className="text-[#282622] font-medium">{getProfileById(profile2)?.name}</p>
                      <p className="text-[#6B6560] text-xs">{getProfileById(profile2)?.relationship || 'Family'}</p>
                    </div>
                  </div>
                  
                  <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-2">
                    {compatibility.relationship_type || 'Cosmic Connection'}
                  </h3>
                  <p className="text-[#6B6560] max-w-lg mx-auto">
                    {compatibility.summary}
                  </p>
                </div>
              </div>

              {/* Aspect Scores */}
              <div className="cosmic-card p-6">
                <h3 className="font-['Fraunces'] text-xl text-[#282622] mb-6">
                  Compatibility Aspects
                </h3>
                
                <div className="space-y-3">
                  {COMPATIBILITY_ASPECTS.map((aspect) => {
                    const score = compatibility.aspects?.[aspect.key] || Math.floor(Math.random() * 30) + 60;
                    const isExpanded = expandedAspect === aspect.key;
                    
                    return (
                      <div key={aspect.key} className="rounded-xl bg-[#FFF9EF] overflow-hidden">
                        <button
                          onClick={() => setExpandedAspect(isExpanded ? null : aspect.key)}
                          className="w-full p-4 flex items-center justify-between gap-3 hover:bg-[#FCE9D9]/60 transition-colors"
                          data-testid={`aspect-${aspect.key}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="w-10 h-10 rounded-xl bg-[#FCE9D9] flex items-center justify-center shrink-0"><aspect.icon className="w-5 h-5 text-[#C84B32]" /></span>
                            <div className="text-left min-w-0">
                              <p className="text-[#282622] font-medium">{aspect.label}</p>
                              <p className="text-[#8A8279] text-xs">{aspect.description}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                            <div className="hidden sm:block w-32 h-2 bg-[#FCE9D9]/70 rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${score}%` }}
                                transition={{ duration: 1, delay: 0.2 }}
                                className={`h-full rounded-full ${
                                  score >= 70 ? 'bg-green-500' : 
                                  score >= 50 ? 'bg-yellow-500' : 'bg-orange-500'
                                }`}
                              />
                            </div>
                            <span className={`font-bold min-w-[44px] text-right shrink-0 ${getScoreColor(score)}`}>
                              {score}%
                            </span>
                            <ChevronDown className={`w-5 h-5 text-[#6B6560] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </button>
                        
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 pb-4 border-t border-[#282622]/10 pt-3">
                                <p className="text-[#6B6560] text-sm">
                                  {compatibility.aspect_details?.[aspect.key] || 
                                    `The ${aspect.label.toLowerCase()} between ${getProfileById(profile1)?.name} and ${getProfileById(profile2)?.name} shows ${score >= 70 ? 'strong' : score >= 50 ? 'moderate' : 'developing'} alignment. This indicates ${score >= 70 ? 'natural harmony and understanding' : score >= 50 ? 'room for growth with effort' : 'an area that requires conscious attention'} in this aspect of your relationship.`}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Strengths & Challenges */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="cosmic-card p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h3 className="font-['Fraunces'] text-lg text-[#282622]">Strengths</h3>
                  </div>
                  <ul className="space-y-3">
                    {(compatibility.strengths || [
                      'Strong emotional understanding',
                      'Shared spiritual values',
                      'Good communication potential'
                    ]).map((strength, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#6B6560] text-sm">
                        <Star className="w-4 h-4 text-[#C84B32] mt-0.5 flex-shrink-0" />
                        {strength}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="cosmic-card p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                    <h3 className="font-['Fraunces'] text-lg text-[#282622]">Areas for Growth</h3>
                  </div>
                  <ul className="space-y-3">
                    {(compatibility.challenges || [
                      'May need patience in disagreements',
                      'Different approaches to problems',
                      'Communication styles vary'
                    ]).map((challenge, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#6B6560] text-sm">
                        <TrendingUp className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                        {challenge}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Advice */}
              <div className="cosmic-card p-6">
                <h3 className="font-['Fraunces'] text-lg text-[#282622] mb-4">
                  Cosmic Advice
                </h3>
                <div className="p-4 rounded-xl bg-gradient-to-r from-[#C84B32]/10 to-[#F5B942]/10 border border-[#C84B32]/20">
                  <p className="text-[#282622] italic">
                    "{compatibility.advice || 'Every relationship is a cosmic dance of energies. Focus on understanding rather than changing each other. The stars show potential, but your choices shape the reality of your bond.'}"
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        )}

        {/* Not Enough Profiles */}
        {profiles.length < 2 && !loading && (
          <div className="cosmic-card p-10 text-center">
            <Users className="w-16 h-16 text-[#C84B32] mx-auto mb-6" />
            <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-3">
              Add More Family Members
            </h3>
            <p className="text-[#6B6560] mb-6">
              You need at least 2 profiles in your family tree to analyze compatibility
            </p>
            <Button
              onClick={() => navigate('/profiles')}
              className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white"
            >
              Go to Family Tree
            </Button>
          </div>
        )}
      </main>
      <ShubhBottomNav />
    </div>
  );
};

export default FamilyCompatibilityPage;
